import { x25519 } from '@noble/curves/ed25519';
import {
  decodeBase64,
  encodeBase64,
  encryptAesGcm,
  decryptAesGcm,
  importAesKey,
  packEncryptedBlob,
  randomBytes,
  unpackEncryptedBlob,
} from './primitives';
import { unwrapKey, wrapKey } from './wrappedKey';

const DESTINATION_FORMAT_VERSION = 0x01;

export function smsDestinationAad(reference: string): string {
  return `sms-destination-v1:${reference}`;
}

export async function encryptSmsDestination(
  phoneE164: string,
  userPublicKey: Uint8Array,
  reference: string,
): Promise<string> {
  const aad = smsDestinationAad(reference);
  const dek = randomBytes(32);
  const wrappedDek = await wrapKey(dek, userPublicKey, aad);
  const aesKey = await importAesKey(dek);
  const packedPhone = packEncryptedBlob(
    await encryptAesGcm(aesKey, new TextEncoder().encode(phoneE164), new TextEncoder().encode(aad)),
  );

  const output = new Uint8Array(1 + 2 + wrappedDek.length + packedPhone.length);
  output[0] = DESTINATION_FORMAT_VERSION;
  output[1] = (wrappedDek.length >> 8) & 0xff;
  output[2] = wrappedDek.length & 0xff;
  output.set(wrappedDek, 3);
  output.set(packedPhone, 3 + wrappedDek.length);
  return encodeBase64(output);
}

export async function decryptSmsDestination(
  destinationCiphertextBase64: string,
  userPrivateKey: Uint8Array,
  reference: string,
): Promise<string> {
  const bytes = decodeBase64(destinationCiphertextBase64);
  if (bytes.length < 4 || bytes[0] !== DESTINATION_FORMAT_VERSION) {
    throw new Error('unsupported_sms_destination_ciphertext');
  }

  const wrappedLen = (bytes[1] << 8) | bytes[2];
  if (wrappedLen <= 0 || 3 + wrappedLen >= bytes.length) {
    throw new Error('invalid_sms_destination_ciphertext');
  }

  const wrappedDek = bytes.slice(3, 3 + wrappedLen);
  const packedPhone = bytes.slice(3 + wrappedLen);
  const aad = smsDestinationAad(reference);
  const dek = await unwrapKey(wrappedDek, userPrivateKey, aad);
  const aesKey = await importAesKey(dek);
  const phoneBytes = await decryptAesGcm(aesKey, unpackEncryptedBlob(packedPhone), new TextEncoder().encode(aad));
  return new TextDecoder().decode(phoneBytes);
}

export function userPublicKeyFromPrivate(userPrivateKey: Uint8Array): Uint8Array {
  return x25519.getPublicKey(userPrivateKey);
}
